import { useState } from 'react';
import type { Ticket, Tech } from '../../types/ticket';
import { TicketStatus } from '../../types/ticket';
import { TicketCard } from '../TicketCard/TicketCard';
import { sortTicketsByPriority } from '../../utils/priorityScore';
import './MyTickets.css';

type TabType = 'active' | 'onhold' | 'complete';

interface MyTicketsProps {
  tickets: Ticket[];
  techs: Tech[];
  currentUserId: string;
  onStart: (ticketId: string) => void;
  onHold: (ticketId: string, reason?: string) => void;
  onComplete?: (ticketId: string, notes?: string) => void;
  onReopen?: (ticketId: string) => void;
  onReassign: (ticketId: string) => void;
  onChangePriority: (ticketId: string) => void;
  onOpenFullView?: (ticketId: string) => void;
}

export const MyTickets: React.FC<MyTicketsProps> = ({
  tickets,
  techs,
  currentUserId,
  onStart,
  onHold,
  onComplete,
  onReopen,
  onReassign,
  onChangePriority,
  onOpenFullView,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabType>('active');

  const myTickets = tickets.filter((t) => t.assignedTechId === currentUserId);

  // Filter tickets based on selected tab
  const getFilteredTickets = () => {
    switch (selectedTab) {
      case 'active':
        return myTickets.filter(
          (t) => t.status === TicketStatus.NEW ||
                 t.status === TicketStatus.IN_PROGRESS
        );
      case 'onhold':
        return myTickets.filter((t) => t.status === TicketStatus.WAITING);
      case 'complete':
        return myTickets.filter((t) => t.status === TicketStatus.RESOLVED);
      default:
        return myTickets;
    }
  };

  const filteredTickets = getFilteredTickets();

  // Sort by urgency score (priority + age + SLA)
  const sortedTickets = sortTicketsByPriority(filteredTickets);

  const getTech = (techId: string | null) => {
    if (!techId) return null;
    return techs.find((t) => t.id === techId) || null;
  };

  // Count tickets by status for tab badges
  const activeCount = myTickets.filter(
    (t) => t.status === TicketStatus.NEW ||
           t.status === TicketStatus.IN_PROGRESS
  ).length;
  const onHoldCount = myTickets.filter((t) => t.status === TicketStatus.WAITING).length;
  const completeCount = myTickets.filter((t) => t.status === TicketStatus.RESOLVED).length;

  return (
    <div className="my-tickets">
      <div className="my-tickets__header">
        <h2 className="my-tickets__title">
          My Jobs
          <span className="my-tickets__count">{myTickets.length}</span>
        </h2>
        <p className="my-tickets__subtitle">
          Sorted by Urgency Score (Priority + Age + SLA)
        </p>
      </div>

      {/* Tabs */}
      <div className="my-tickets__tabs">
        <button
          className={`my-tickets__tab ${selectedTab === 'active' ? 'my-tickets__tab--active' : ''}`}
          onClick={() => setSelectedTab('active')}
        >
          <span className="my-tickets__tab-label">Active</span>
          <span className="my-tickets__tab-count">{activeCount}</span>
        </button>
        <button
          className={`my-tickets__tab ${selectedTab === 'onhold' ? 'my-tickets__tab--active' : ''}`}
          onClick={() => setSelectedTab('onhold')}
        >
          <span className="my-tickets__tab-label">On-Hold</span>
          <span className="my-tickets__tab-count">{onHoldCount}</span>
        </button>
        <button
          className={`my-tickets__tab ${selectedTab === 'complete' ? 'my-tickets__tab--active' : ''}`}
          onClick={() => setSelectedTab('complete')}
        >
          <span className="my-tickets__tab-label">Complete</span>
          <span className="my-tickets__tab-count">{completeCount}</span>
        </button>
        <div className="my-tickets__tab-indicator" data-tab={selectedTab} />
      </div>

      <div className="my-tickets__list">
        {sortedTickets.length === 0 ? (
          <div className="my-tickets__empty">
            <svg
              className="my-tickets__empty-icon"
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M24 44c11.046 0 20-8.954 20-20S35.046 4 24 4 4 12.954 4 24s8.954 20 20 20z"
                strokeWidth="2"
              />
              <path
                d="M16 20h16M16 28h10"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <p className="my-tickets__empty-text">No tickets assigned to you</p>
            <p className="my-tickets__empty-subtext">
              Check the team queue to pick up available tickets
            </p>
          </div>
        ) : (
          sortedTickets.map((ticket, index) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              tech={getTech(ticket.assignedTechId)}
              isHighlighted={index === 0}
              onComplete={onComplete}
              onReopen={onReopen}
              isCollapsed={index !== 0}
              onStart={onStart}
              onHold={onHold}
              onReassign={onReassign}
              onChangePriority={onChangePriority}
              onOpenFullView={onOpenFullView}
            />
          ))
        )}
      </div>
    </div>
  );
};
